import { useState, useEffect } from 'react'
import { clsx } from 'clsx'
import { Calendar, User, CheckCircle2, Circle } from 'lucide-react'

interface TaskSnapshot {
  title: string
  status: string
  priority: string
  assigneeName?: string
  dueDate?: string
}

interface ChatTaskCardProps {
  snapshot: TaskSnapshot
  taskRef?: string
  groupId?: string
  onToggle?: (taskId: string, currentStatus: 'pending' | 'completed') => void
}

const priorityColors: Record<string, string> = {
  high: 'var(--color-danger-500)',
  medium: 'var(--color-warning-500)',
  low: 'var(--color-primary-500)',
  none: 'var(--border-default, #d1d5db)',
}

const statusLabels: Record<string, string> = {
  pending: '진행 중',
  completed: '완료',
}

export function ChatTaskCard({ snapshot, taskRef, groupId, onToggle }: ChatTaskCardProps) {
  const [liveSnapshot, setLiveSnapshot] = useState(snapshot)

  // 실시간 구독: taskRef가 있으면 Firestore에서 라이브 데이터 수신
  useEffect(() => {
    if (!taskRef || !groupId) return
    let cancelled = false
    let unsub: (() => void) | undefined

    const subscribe = async () => {
      try {
        const { getFirestoreUtils } = await import('@/lib/firebase')
        if (cancelled) return
        const { firestore, doc, onSnapshot } = await getFirestoreUtils()
        if (cancelled) return
        unsub = onSnapshot(
          doc(firestore, `sharedGroups/${groupId}/tasks/${taskRef}`),
          (snap) => {
            if (cancelled || !snap.exists()) return
            const data = snap.data()
            setLiveSnapshot({
              title: data.title,
              status: data.status,
              priority: data.priority,
              assigneeName: data.assigneeNames?.join(', ') || data.assigneeName,
              dueDate: data.dueDate,
            })
          }
        )
      } catch (err) {
        console.error('Task subscription failed:', err)
      }
    }
    subscribe()
    return () => { cancelled = true; unsub?.() }
  }, [taskRef, groupId])

  const borderColor = priorityColors[liveSnapshot.priority] || priorityColors.none
  const isCompleted = liveSnapshot.status === 'completed'

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (onToggle && taskRef) {
      try {
        onToggle(taskRef, liveSnapshot.status as 'pending' | 'completed')
      } catch (err) {
        console.error('Task toggle failed:', err)
      }
    }
  }

  return (
    <div
      className={clsx(
        'p-3 rounded-lg border border-base bg-surface-primary border-l-4',
        isCompleted && 'opacity-70'
      )}
      style={{ borderLeftColor: borderColor }}
    >
      <div className="flex items-start gap-2">
        {/* 완료 토글 버튼 */}
        {onToggle && taskRef && (
          <button
            onClick={handleToggle}
            className="flex-shrink-0 mt-0.5"
            title={isCompleted ? '미완료로 변경' : '완료로 변경'}
          >
            {isCompleted ? (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            ) : (
              <Circle className="w-4 h-4 text-caption hover:text-primary-500 transition-colors" />
            )}
          </button>
        )}

        <div className="flex-1 min-w-0">
          <span
            className={clsx(
              'text-sm font-medium',
              isCompleted ? 'text-caption line-through' : 'text-heading'
            )}
          >
            {liveSnapshot.title}
          </span>

          <div className="flex items-center gap-2 mt-1 text-xs text-caption flex-wrap">
            <span
              className={clsx(
                'px-1.5 py-0.5 rounded text-meta font-medium',
                isCompleted
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-surface-tertiary text-caption'
              )}
            >
              {statusLabels[liveSnapshot.status] || liveSnapshot.status}
            </span>

            {liveSnapshot.assigneeName && (
              <span className="flex items-center gap-0.5">
                <User className="w-3 h-3" />
                {liveSnapshot.assigneeName}
              </span>
            )}

            {liveSnapshot.dueDate && (
              <span className="flex items-center gap-0.5">
                <Calendar className="w-3 h-3" />
                {liveSnapshot.dueDate}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
